import { useCallback, useEffect, useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../../../../components/ui/card";
import { Button } from "../../../../components/ui/button";
import { Input } from "../../../../components/ui/input";
import { Label } from "../../../../components/ui/label";
import { Switch } from "../../../../components/ui/switch";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../../../components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../../components/ui/select";
import {
  ArrowLeft,
  AlertTriangle,
  BellPlus,
  CheckCircle2,
  KeyRound,
  Lightbulb,
  Loader2,
  LockKeyhole,
  PlugZap,
  ScrollText,
  SearchCheck,
  Trash2,
} from "lucide-react";
import { SiGooglegemini, SiOpenai } from "react-icons/si";
import { useToast } from "../../../../hooks/use-toast";
import { SettingProps } from "../interface";
import {
  deleteLLMApiKey,
  getLLMFeatureStatuses,
  getLLMApiKeys,
  getLLMProviderModels,
  LLMApiKeyConfiguration,
  LLMFeature,
  LLMFeatureStatus,
  LLMProvider,
  saveLLMApiKey,
  updateLLMFeatureStatus,
  updateLLMProviderActivation,
  updateLLMProviderModel,
} from "../Api/settingsApi";

const ClaudeSplashIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 155 149" aria-hidden="true" focusable="false">
    <g transform="translate(-75.96,-223.53)">
      <path
        fill="currentColor"
        d="m 105.01,322.07 29.14,-16.35 0.49,-1.42 -0.49,-0.79 h -1.42 l -4.87,-0.3 -16.65,-0.45 -14.44,-0.6 -13.99,-0.75 -3.52,-0.75 -3.3,-4.35 0.34,-2.17 2.96,-1.99 4.24,0.37 9.37,0.64 14.06,0.97 10.2,0.6 15.11,1.57 h 2.4 l 0.34,-0.97 -0.82,-0.6 -0.64,-0.6 -14.55,-9.86 -15.75,-10.42 -8.25,-6 -4.46,-3.04 -2.25,-2.85 -0.97,-6.22 4.05,-4.46 5.44,0.37 1.39,0.37 5.51,4.24 11.77,9.11 15.37,11.32 2.25,1.87 0.9,-0.64 0.11,-0.45 -1.01,-1.69 -8.36,-15.11 -8.92,-15.37 -3.97,-6.37 -1.05,-3.82 c -0.37,-1.57 -0.64,-2.89 -0.64,-4.5 l 4.61,-6.26 2.55,-0.82 6.15,0.82 2.59,2.25 3.82,8.74 6.19,13.76 9.6,18.71 2.81,5.55 1.5,5.14 0.56,1.57 h 0.97 v -0.9 l 0.79,-10.54 1.46,-12.94 1.42,-16.65 0.49,-4.69 2.32,-5.62 4.61,-3.04 3.6,1.72 2.96,4.24 -0.41,2.74 -1.76,11.44 -3.45,17.92 -2.25,12 h 1.31 l 1.5,-1.5 6.07,-8.06 10.2,-12.75 4.5,-5.06 5.25,-5.59 3.37,-2.66 h 6.37 l 4.69,6.97 -2.1,7.2 -6.56,8.32 -5.44,7.05 -7.8,10.5 -4.87,8.4 0.45,0.67 1.16,-0.11 17.62,-3.75 9.52,-1.72 11.36,-1.95 5.14,2.4 0.56,2.44 -2.02,4.99 -12.15,3 -14.25,2.85 -21.22,5.02 -0.26,0.19 0.3,0.37 9.56,0.9 4.09,0.22 h 10.01 l 18.64,1.39 4.87,3.22 2.92,3.94 -0.49,3 -7.5,3.82 -10.12,-2.4 -23.62,-5.62 -8.1,-2.02 h -1.12 v 0.67 l 6.75,6.6 12.37,11.17 15.49,14.4 0.79,3.56 -1.99,2.81 -2.1,-0.3 -13.61,-10.24 -5.25,-4.61 -11.89,-10.01 h -0.79 v 1.05 l 2.74,4.01 14.47,21.75 0.75,6.67 -1.05,2.17 -3.75,1.31 -4.12,-0.75 -8.47,-11.89 -8.74,-13.39 -7.05,-12 -0.86,0.49 -4.16,44.81 -1.95,2.29 -4.5,1.72 -3.75,-2.85 -1.99,-4.61 1.99,-9.11 2.4,-11.89 1.95,-9.45 1.76,-11.74 1.05,-3.9 -0.07,-0.26 -0.86,0.11 -8.85,12.15 -13.46,18.19 -10.65,11.4 -2.55,1.01 -4.42,-2.29 0.41,-4.09 2.47,-3.64 14.74,-18.75 8.89,-11.62 5.74,-6.71 -0.04,-0.97 h -0.34 l -39.15,25.42 -6.97,0.9 -3,-2.81 0.37,-4.61 1.42,-1.5 11.77,-8.1 -0.04,0.04 z"
      />
    </g>
  </svg>
);

const providerOptions: Array<{
  value: LLMProvider;
  label: string;
  icon: any;
  iconClass: string;
  iconFrameClass: string;
  disabled?: boolean;
}> = [
    {
      value: "OPENAI",
      label: "OpenAI",
      icon: SiOpenai,
      iconClass: "text-emerald-600 dark:text-emerald-300",
      iconFrameClass: "border-emerald-100 bg-emerald-50 dark:border-emerald-900 dark:bg-emerald-950/40",
    },
    {
      value: "CLAUDE",
      label: "Claude",
      icon: ClaudeSplashIcon,
      iconClass: "text-amber-600 dark:text-amber-300",
      iconFrameClass: "border-orange-100 bg-orange-50 dark:border-orange-900 dark:bg-orange-950/40",
    },
    {
      value: "GEMINI",
      label: "Gemini",
      icon: SiGooglegemini,
      iconClass: "text-sky-600 dark:text-sky-300",
      iconFrameClass: "border-sky-100 bg-sky-50 dark:border-sky-900 dark:bg-sky-950/40",
      disabled: true,
    },
  ];

const featureGroups = [
  {
    feature: "POLICY_CREATION" as LLMFeature,
    title: "Policy Creation",
    icon: ScrollText,
    borderClass: "border-l-indigo-500",
    iconClass: "bg-indigo-50 text-indigo-700 dark:bg-indigo-950/40 dark:text-indigo-300",
    points: [
      "Create cloud policies for AWS, GCP, and Azure.",
      "Supports guardrail policies for preventive controls.",
      "Supports remediation policies for corrective action.",
    ],
  },
  {
    feature: "ALERT_CREATION" as LLMFeature,
    title: "Alert Creation",
    icon: BellPlus,
    borderClass: "border-l-rose-500",
    iconClass: "bg-rose-50 text-rose-700 dark:bg-rose-950/40 dark:text-rose-300",
    points: [
      "Create cloud alerts for GCP and Azure.",
      "Supports behavior, rule and sequential alerts.",
      "Helps admins convert requirements into actionable alert logic.",
    ],
  },
  {
    feature: "BEHAVIOUR_ALERT_DETECTION" as LLMFeature,
    title: "Behavior Alert Detection",
    icon: SearchCheck,
    borderClass: "border-l-emerald-500",
    iconClass: "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300",
    points: [
      "Reviews user activity patterns with LLM analysis.",
      "Detects meaningful behavioral changes across activity history.",
      "Highlights anomalies that may need investigation.",
    ],
  },
  {
    feature: "LOG_EVENT_SUMMARY_RISK_INSIGHTS" as LLMFeature,
    title: "Log & Event Summary, Risk & Insights Builder",
    icon: Lightbulb,
    borderClass: "border-l-violet-500",
    iconClass: "bg-violet-50 text-violet-700 dark:bg-violet-950/40 dark:text-violet-300",
    points: [
      "Summarizes logs and key security events.",
      "Covers blast radius, SaaS correlation, alerts, and policies.",
      "Generates risk and insight summaries for cloud entities.",
    ],
  },
];

const providerByValue = providerOptions.reduce<Record<LLMProvider, (typeof providerOptions)[number]>>(
  (acc, provider) => {
    acc[provider.value] = provider;
    return acc;
  },
  {} as Record<LLMProvider, (typeof providerOptions)[number]>
);

const hasStoredKey = (configuration?: LLMApiKeyConfiguration) =>
  Boolean(configuration?.hasApiKey);

const defaultFeatureStatuses: Record<LLMFeature, boolean> = {
  POLICY_CREATION: true,
  ALERT_CREATION: true,
  BEHAVIOUR_ALERT_DETECTION: false,
  LOG_EVENT_SUMMARY_RISK_INSIGHTS: true,
};

export default function LLMSettings({ onBack }: SettingProps) {
  const { toast } = useToast();
  const [selectedProvider, setSelectedProvider] = useState<LLMProvider>("OPENAI");
  const [apiKey, setApiKey] = useState("");
  const [apiKeys, setApiKeys] = useState<LLMApiKeyConfiguration[]>([]);
  const [providerModels, setProviderModels] = useState<Partial<Record<LLMProvider, string[]>>>({});
  const [featureStatuses, setFeatureStatuses] = useState<Record<LLMFeature, boolean>>(defaultFeatureStatuses);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [loadingModels, setLoadingModels] = useState(false);
  const [updatingModel, setUpdatingModel] = useState(false);
  const [updatingProvider, setUpdatingProvider] = useState<LLMProvider | null>(null);
  const [updatingFeature, setUpdatingFeature] = useState<LLMFeature | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const hasAnyProvider = useMemo(() => apiKeys.some((configuration) => hasStoredKey(configuration)), [apiKeys]);
  const hasActiveProvider = useMemo(
    () => apiKeys.some((configuration) => hasStoredKey(configuration) && configuration.active),
    [apiKeys]
  );
  const selectedProviderMeta = providerByValue[selectedProvider];
  const selectedProviderConfiguration = useMemo(
    () => apiKeys.find((configuration) => configuration.provider === selectedProvider),
    [apiKeys, selectedProvider]
  );
  const selectedProviderConfigured = hasStoredKey(selectedProviderConfiguration);

  const loadLLMSettings = useCallback(async () => {
    setLoading(true);
    try {
      const [keys, statuses] = await Promise.all([
        getLLMApiKeys(),
        getLLMFeatureStatuses(),
      ]);
      setApiKeys(keys || []);
      setFeatureStatuses((current) => (statuses || []).reduce(
        (result: Record<LLMFeature, boolean>, status: LLMFeatureStatus) => {
          result[status.feature] = Boolean(status.enabled);
          return result;
        },
        { ...current }
      ));
    } catch (error) {
      console.error(error);
      toast({
        title: "Unable to load LLM setup",
        description: "The current LLM configuration could not be loaded.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    loadLLMSettings();
  }, [loadLLMSettings]);

  useEffect(() => {
    if (!selectedProviderConfigured || providerModels[selectedProvider]) {
      return;
    }
    let active = true;
    setLoadingModels(true);
    getLLMProviderModels(selectedProvider)
      .then((models) => {
        if (active) {
          setProviderModels((current) => ({ ...current, [selectedProvider]: models || [] }));
        }
      })
      .catch((error) => {
        console.error(error);
        if (active) {
          setProviderModels((current) => ({ ...current, [selectedProvider]: [] }));
        }
      })
      .finally(() => {
        if (active) {
          setLoadingModels(false);
        }
      });
    return () => {
      active = false;
    };
  }, [providerModels, selectedProvider, selectedProviderConfigured]);

  const handleProviderChange = (value: LLMProvider) => {
    if (providerByValue[value]?.disabled) {
      return;
    }
    setSelectedProvider(value);
    setApiKey("");
  };

  const handleSave = async (event: React.FormEvent) => {
    event.preventDefault();
    if (selectedProviderConfigured) {
      toast({
        title: "API key already configured",
        description: `Delete the existing ${selectedProviderMeta.label} API key before adding a new one.`,
        variant: "destructive",
      });
      return;
    }

    if (!apiKey.trim()) {
      toast({
        title: "Enter a provider key",
        description: "This field is needed before saving.",
        variant: "destructive",
      });
      return;
    }

    setSaving(true);
    try {
      const saved = await saveLLMApiKey({
        provider: selectedProvider,
        name: selectedProviderMeta.label,
        apiKey: apiKey.trim(),
        active: !hasActiveProvider,
      });
      setProviderModels((current) => ({
        ...current,
        [selectedProvider]: saved.availableModels || [],
      }));
      setApiKey("");
      await loadLLMSettings();
      toast({
        title: "Provider saved",
        description: `${selectedProviderMeta.label} is configured${saved.active ? " and active" : ""}.`,
      });
    } catch (error) {
      console.error(error);
      toast({
        title: "Unable to save provider",
        description: "Check the key and try again.",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await deleteLLMApiKey(selectedProvider);
      setApiKey("");
      setProviderModels((current) => {
        const next = { ...current };
        delete next[selectedProvider];
        return next;
      });
      await loadLLMSettings();
      setDeleteDialogOpen(false);
      toast({
        title: "API key deleted",
        description: `${selectedProviderMeta.label} API key was removed.`,
      });
    } catch (error) {
      console.error(error);
      toast({
        title: "Unable to delete API key",
        description: "The configured key could not be removed.",
        variant: "destructive",
      });
    } finally {
      setDeleting(false);
    }
  };

  const handleProviderActivation = async (provider: LLMProvider, active: boolean) => {
    setUpdatingProvider(provider);
    try {
      await updateLLMProviderActivation(provider, active);
      await loadLLMSettings();
      toast({
        title: active ? "Provider enabled" : "Provider disabled",
        description: active
          ? `${providerByValue[provider].label} is now used for LLM features.`
          : `${providerByValue[provider].label} is no longer active.`,
      });
    } catch (error) {
      console.error(error);
      toast({
        title: "Unable to update provider",
        description: "The active provider could not be changed.",
        variant: "destructive",
      });
    } finally {
      setUpdatingProvider(null);
    }
  };

  const handleModelChange = async (model: string) => {
    if (!model?.trim()) {
      return;
    }
    setUpdatingModel(true);
    try {
      const updated = await updateLLMProviderModel(selectedProvider, model);
      setApiKeys((current) => current.map((configuration) =>
        configuration.provider === selectedProvider ? { ...configuration, ...updated } : configuration
      ));
      toast({
        title: "Model updated",
        description: `${selectedProviderMeta.label} will use ${model}.`,
      });
    } catch (error) {
      console.error(error);
      toast({
        title: "Unable to update model",
        description: "The selected model could not be saved.",
        variant: "destructive",
      });
    } finally {
      setUpdatingModel(false);
    }
  };

  const handleFeatureToggle = async (feature: LLMFeature, enabled: boolean) => {
    setUpdatingFeature(feature);
    try {
      const updated = await updateLLMFeatureStatus(feature, enabled);
      setFeatureStatuses((current) => ({
        ...current,
        [updated.feature]: Boolean(updated.enabled),
      }));
    } catch (error) {
      console.error(error);
      toast({
        title: "Unable to update feature",
        description: "The feature status could not be saved.",
        variant: "destructive",
      });
    } finally {
      setUpdatingFeature(null);
    }
  };

  return (
    <div className="tenant-settings-page flex-1 space-y-6 p-6">
      <Card className="settings-header-card border-border/40 rounded-2xl">
        <CardContent className="p-5 sm:p-6">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="outline"
                size="icon"
                onClick={onBack}
                className="settings-back-button"
                aria-label="Back to settings"
                title="Back to settings"
              >
                <ArrowLeft className="h-4 w-4" />
              </Button>
              <div>
                <h1 className="text-2xl font-bold tracking-tight">LLM Setup</h1>
                <p className="text-muted-foreground">
                  Configure model provider access for LLM features.
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(360px,430px)_1fr]">
        <Card className="border-border/60 rounded-2xl">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <KeyRound className="h-4 w-4" />
              Provider Access
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-5">
            <form className="space-y-5" onSubmit={handleSave}>
              <div className="space-y-2">
                <Label>Provider</Label>
                <Select value={selectedProvider} onValueChange={(value) => handleProviderChange(value as LLMProvider)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {providerOptions.map((provider) => {
                      const ProviderIcon = provider.icon;
                      return (
                        <SelectItem key={provider.value} value={provider.value} disabled={provider.disabled}>
                          <span className="inline-flex items-center gap-2">
                            <ProviderIcon className={`h-4 w-4 ${provider.iconClass}`} />
                            {provider.label}
                            {provider.disabled && <span className="text-xs text-muted-foreground">(Coming soon)</span>}
                          </span>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>API key</Label>
                <div className="relative">
                  <LockKeyhole className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    type={selectedProviderConfigured ? "text" : "password"}
                    value={selectedProviderConfigured ? "**********" : apiKey}
                    onChange={(event) => setApiKey(event.target.value)}
                    placeholder={`${selectedProviderMeta.label} API key`}
                    disabled={selectedProviderConfigured}
                    readOnly={selectedProviderConfigured}
                    className="pl-10"
                  />
                </div>
              </div>

              {selectedProviderConfigured && (
                <div className="space-y-2">
                  <Label>Model</Label>
                  <Select
                    value={selectedProviderConfiguration?.model}
                    onValueChange={(model) => void handleModelChange(model)}
                    disabled={loadingModels || updatingModel}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={loadingModels ? "Loading models..." : "Select model"} />
                    </SelectTrigger>
                    <SelectContent>
                      {(providerModels[selectedProvider] || []).map((model) => (
                        <SelectItem key={model} value={model}>
                          {model}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {!selectedProviderConfigured && (
                <Button type="submit" disabled={saving} className="w-full gap-2">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <PlugZap className="h-4 w-4" />}
                  Save Provider
                </Button>
              )}

              {selectedProviderConfigured && (
                <Button
                  type="button"
                  variant="destructive"
                  disabled={deleting}
                  className="w-full gap-2"
                  onClick={() => setDeleteDialogOpen(true)}
                >
                  {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                  Delete API Key
                </Button>
              )}

              {!loading && !hasAnyProvider && (
                <div className="inline-flex w-fit items-center gap-2 rounded-full bg-amber-50 px-3 py-2 text-sm font-medium text-amber-800 dark:bg-amber-950/40 dark:text-amber-200">
                  <KeyRound className="h-4 w-4" />
                  Add at least one provider API key to enable LLM features.
                </div>
              )}

              {!loading && hasAnyProvider && !hasActiveProvider && (
                <div className="inline-flex w-fit items-center gap-2 rounded-full bg-amber-50 px-3 py-2 text-sm font-medium text-amber-800 dark:bg-amber-950/40 dark:text-amber-200">
                  <KeyRound className="h-4 w-4" />
                  Enable one provider to use LLM features.
                </div>
              )}
            </form>

            <div className="settings-muted-card rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700 dark:border-slate-800 dark:bg-slate-950/40 dark:text-slate-300">
              <div className="flex justify-center">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-slate-100 text-slate-600 dark:bg-slate-900 dark:text-slate-300">
                  <AlertTriangle className="h-5 w-5" />
                </div>
              </div>
              <ul className="mt-3 space-y-2 leading-relaxed">
                <li className="flex gap-2">
                  <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-slate-400 dark:bg-slate-500" />
                  <span>LLM outputs can require admin review before applying security changes.</span>
                </li>
                <li className="flex gap-2">
                  <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-slate-400 dark:bg-slate-500" />
                  <span>Generated recommendations should be validated against your organization policies.</span>
                </li>
                <li className="flex gap-2">
                  <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-slate-400 dark:bg-slate-500" />
                  <span>Provider usage, retention, and billing follow the selected provider account settings.</span>
                </li>
              </ul>
            </div>
          </CardContent>
        </Card>

        <div className="space-y-6">
          <section className="space-y-3">
            <div className="flex items-center justify-between gap-4">
              <h2 className="text-sm font-semibold uppercase text-muted-foreground">
                Providers
              </h2>
              {loading && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
            </div>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
              {providerOptions.map((provider) => {
                const ProviderIcon = provider.icon;
                const configuration = apiKeys.find((key) => key.provider === provider.value);
                const configured = hasStoredKey(configuration);
                const active = Boolean(configuration?.active);
                return (
                  <Card
                    key={provider.value}
                    className={`border-border/60 rounded-2xl ${provider.disabled ? "opacity-70" : "cursor-pointer hover-elevate"} ${
                      selectedProvider === provider.value ? "ring-1 ring-primary/30" : ""
                    }`}
                    onClick={() => !provider.disabled && handleProviderChange(provider.value)}
                  >
                    <CardContent className="flex items-center justify-between p-4">
                      <div className="flex items-center gap-3">
                        <div className={`flex h-10 w-10 items-center justify-center rounded-xl border ${provider.iconFrameClass}`}>
                          <ProviderIcon className={`h-5 w-5 ${provider.iconClass}`} />
                        </div>
                        <div>
                          <span className="font-medium">{provider.label}</span>
                          <p className="text-xs text-muted-foreground">
                            {provider.disabled ? "Coming soon" : active ? "Active" : configured ? "Configured" : "Not configured"}
                          </p>
                        </div>
                      </div>
                      {configured && !provider.disabled ? (
                        <div className="flex items-center gap-2">
                          {updatingProvider === provider.value && <Loader2 className="h-4 w-4 animate-spin" />}
                          <Switch
                            checked={active}
                            disabled={updatingProvider !== null}
                            onClick={(event) => event.stopPropagation()}
                            onCheckedChange={(checked) => void handleProviderActivation(provider.value, checked)}
                            aria-label={`${active ? "Disable" : "Enable"} ${provider.label}`}
                          />
                        </div>
                      ) : configured ? (
                        <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                      ) : (
                        <span className="h-2.5 w-2.5 rounded-full bg-slate-300 dark:bg-slate-700" />
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </section>

          <section className="space-y-3">
            <h2 className="text-sm font-semibold uppercase text-muted-foreground">
              LLM Features
            </h2>
            <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
              {featureGroups.map((feature) => {
                const FeatureIcon = feature.icon;
                return (
                  <Card
                    key={feature.title}
                    className={`min-h-[255px] border-l-4 border-border rounded-2xl ${feature.borderClass} ${hasActiveProvider ? "opacity-100" : "opacity-75"
                      }`}
                  >
                    <CardContent className="flex h-full flex-col p-5">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex items-start gap-3">
                          <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${feature.iconClass}`}>
                            <FeatureIcon className="h-5 w-5" />
                          </div>
                          <p className="text-base font-semibold leading-tight text-foreground">{feature.title}</p>
                        </div>
                        <div className="flex shrink-0 items-center gap-2">
                          {updatingFeature === feature.feature && <Loader2 className="h-4 w-4 animate-spin" />}
                          <Switch
                            checked={featureStatuses[feature.feature]}
                            disabled={updatingFeature !== null}
                            onCheckedChange={(checked) => void handleFeatureToggle(feature.feature, checked)}
                            aria-label={`${featureStatuses[feature.feature] ? "Disable" : "Enable"} ${feature.title}`}
                          />
                        </div>
                      </div>
                      <ul className="mt-5 space-y-3 text-sm leading-relaxed text-muted-foreground">
                        {feature.points.map((point) => (
                          <li key={point} className="flex gap-2">
                            <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-slate-400 dark:bg-slate-500" />
                            <span>{point}</span>
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </section>
        </div>
      </div>

      <AlertDialog open={deleteDialogOpen} onOpenChange={(open) => !deleting && setDeleteDialogOpen(open)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              Delete API Key
            </AlertDialogTitle>
            <AlertDialogDescription>
              Remove the stored API key for {selectedProviderMeta.label}? LLM features using this provider will stop working until a new key is saved.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={deleting}
              onClick={(event) => {
                event.preventDefault();
                void handleDelete();
              }}
            >
              {deleting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting
                </>
              ) : (
                "Delete"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
